import { Button } from 'components/buttons/button';
import {
  RichUtils,
  EditorState,
  ContentBlock,
  ContentState,
  getVisibleSelectionRect,
  SelectionState,
} from 'draft-js';
import { useInput } from 'lib/hooks/use-input';
import { useHasClickedOutside } from 'lib/hooks/use-has-clicked-outside';
import {
  BaseSyntheticEvent,
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import { constants, getEntityIfSelected } from '../editor-utils';

export const linkStrategy = (
  contentBlock: ContentBlock,
  callback: (start: number, end: number) => void,
  contentState: ContentState
) => {
  contentBlock.findEntityRanges((character) => {
    const entityKey = character.getEntity();
    return (
      entityKey !== null &&
      contentState.getEntity(entityKey).getType() === constants.EntityTypes.LINK
    );
  }, callback);
};

interface Props {
  contentState: ContentState;
  entityKey: string;
  children: any;
}

type LinkType = 'link' | 'book' | 'event';

export const Link = ({ contentState, entityKey, children }: Props) => {
  const { url }: { url: string; type: LinkType } = contentState
    .getEntity(entityKey)
    .getData();
  const type = url.split('/')[1];

  if (type === 'book') {
    return (
      <a
        className='bg-red-100 px-1 rounded '
        href={url}
        target='_blank'
        aria-label={url}
        title={url}>
        <span className='mr-1 '>
          <svg
            className='h-3 w-3 my-auto inline'
            xmlns='http://www.w3.org/2000/svg'
            viewBox='0 0 20 20'
            fill='currentColor'>
            <path d='M9 4.804A7.968 7.968 0 005.5 4c-1.255 0-2.443.29-3.5.804v10A7.969 7.969 0 015.5 14c1.669 0 3.218.51 4.5 1.385A7.962 7.962 0 0114.5 14c1.255 0 2.443.29 3.5.804v-10A7.968 7.968 0 0014.5 4c-1.255 0-2.443.29-3.5.804V12a1 1 0 11-2 0V4.804z' />
          </svg>
        </span>
        <span className='cursor-pointer'>{children}</span>
      </a>
    );
  } else if (type === 'events') {
    return (
      <a
        className='bg-green-100 px-1 rounded '
        href={url}
        target='_blank'
        aria-label={url}
        title={url}>
        <span className='mr-1 '>
          <svg
            className='h-3 w-3 my-auto inline'
            xmlns='http://www.w3.org/2000/svg'
            viewBox='0 0 20 20'
            fill='currentColor'>
            <path
              fillRule='evenodd'
              d='M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z'
              clipRule='evenodd'
            />
          </svg>
        </span>
        <span className='cursor-pointer'>{children}</span>
      </a>
    );
  } else {
    return (
      <a
        className='bg-indigo-100 px-1 rounded '
        href={url}
        target='_blank'
        aria-label={url}
        title={url}>
        <span className='mr-1 '>
          <svg
            className='h-3 w-3 my-auto inline'
            xmlns='http://www.w3.org/2000/svg'
            viewBox='0 0 20 20'
            fill='currentColor'>
            <path
              fillRule='evenodd'
              d='M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z'
              clipRule='evenodd'
            />
          </svg>
        </span>
        <span className='cursor-pointer'>{children}</span>
      </a>
    );
  }
};

interface LinkComponentProps {
  editorState: EditorState;
  setEditorState: Dispatch<SetStateAction<EditorState>>;
}
export const LinkComponent = ({
  editorState,
  setEditorState,
}: LinkComponentProps) => {
  const [showModal, setShowModal] = useState<boolean>(false);
  const { value, bind, isModified, init } = useInput('');
  const ref = useRef(null);
  const hasClickedOutside = useHasClickedOutside(ref);
  const alreadyHasLink = getEntityIfSelected(
    constants.EntityTypes.LINK,
    editorState
  );

  useEffect(() => {
    if (showModal && hasClickedOutside) {
      setShowModal(false);
      init('');
    }
  });

  const handleCreateLink = () => {
    CreateLink(editorState, setEditorState, value, 'link');
    setShowModal(false);
    init('');
  };

  return (
    <>
      <div className='relative flex'>
        <button
          title='link'
          aria-label='link'
          className='bg-white hover:bg-gray-400 text-gray-800 font-semibold px-2 rounded disabled:text-gray-400'
          onClick={(e) => {
            e.preventDefault();
            setShowModal(true);
          }}
          disabled={!!alreadyHasLink}>
          <svg
            className='w-5 h-8'
            xmlns='http://www.w3.org/2000/svg'
            viewBox='0 0 20 20'
            fill='currentColor'>
            <path
              fillRule='evenodd'
              d='M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z'
              clipRule='evenodd'
            />
          </svg>
        </button>
        {showModal && (
          <div
            ref={ref}
            className='z-10  absolute right-0 mt-8 rounded-md  ring-1 ring-black ring-opacity-5 w-72 [ bg-white border border-gray-100 shadow-lg sm:rounded-lg px-1 py-2 sm:px-2 space-x-2 flex flex-row ] '>
            <label
              htmlFor='link'
              className='inline whitespace-nowrap my-auto text-sm font-medium text-gray-700'>
              Link to
            </label>
            <div className='mt-1 flex rounded-md shadow-sm re'>
              <div className='relative flex items-stretch flex-grow focus-within:z-10'>
                <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                  <svg
                    className='h-5 w-5 text-gray-500 my-auto inline'
                    xmlns='http://www.w3.org/2000/svg'
                    viewBox='0 0 20 20'
                    fill='currentColor'>
                    <path
                      fillRule='evenodd'
                      d='M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z'
                      clipRule='evenodd'
                    />
                  </svg>
                </div>
                <input
                  {...bind}
                  type='url'
                  name='link'
                  id='link'
                  className='focus:ring-indigo-500 focus:border-indigo-500 block w-full rounded-none rounded-l-md pl-10 sm:text-sm border-gray-300 min-w-full'
                  placeholder='www.url.com'
                  autoComplete='off'
                />
              </div>
              <button
                className='-ml-px relative inline-flex items-center space-x-2 px-4 py-2 border border-gray-300 text-sm font-medium rounded-r-md text-gray-700 bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 disabled:cursor-not-allowed disabled:text-gray-500'
                onClick={handleCreateLink}
                disabled={!isModified}>
                <span>Link</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export const CreateLink = (
  state: EditorState,
  setState: Dispatch<SetStateAction<EditorState>>,
  urlValue: string,
  type: LinkType
) => {
  const contentState = state.getCurrentContent();

  const contentStateWithEntity = contentState.createEntity('LINK', 'MUTABLE', {
    url: urlValue,
    type: type,
  });
  const entityKey = contentStateWithEntity.getLastCreatedEntityKey();

  const newEditorState = EditorState.set(state, {
    currentContent: contentStateWithEntity,
  });

  setState(
    RichUtils.toggleLink(
      newEditorState,
      newEditorState.getSelection(),
      entityKey
    )
  );
};

export const LinkPlugin = {
  strategy: linkStrategy,
  component: Link,
};

export const removeLink = (
  blockKey: string,
  entityKey: string,
  editorState: EditorState,
  updateEditorState: Dispatch<SetStateAction<EditorState>>
) => {
  const content = editorState.getCurrentContent();
  const block = content.getBlockForKey(blockKey);
  const oldSelection = editorState.getSelection();
  block.findEntityRanges(
    (character) => {
      const eKey = character.getEntity();
      return eKey === entityKey;
    },
    (start, end) => {
      const selection = new SelectionState({
        anchorKey: blockKey,
        focusKey: blockKey,
        anchorOffset: start,
        focusOffset: end,
      });
      const newEditorState = EditorState.forceSelection(
        RichUtils.toggleLink(editorState, selection, null),
        oldSelection
      );
      updateEditorState(newEditorState);
    }
  );
};

interface LinkEditProps {
  editorState: EditorState;
  updateEditorState: Dispatch<SetStateAction<EditorState>>;
  url: string;
  blockKey: string;
  entityKey: string;
}

export const LinkEditComponent = ({
  blockKey,
  editorState,
  updateEditorState,
  entityKey,
  url,
}: LinkEditProps) => {
  const [position, setPosition] = useState(null);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    if (ref) {
      calculatePosition();
    }
  }, [url]);

  const getRelativeParent = (element) => {
    if (!element) {
      return null;
    }

    const position = window
      .getComputedStyle(element)
      .getPropertyValue('position');
    if (position !== 'static') {
      return element;
    }

    return getRelativeParent(element.parentElement);
  };

  const calculatePosition = () => {
    if (!ref) {
      return null;
    }

    const relativeParent = getRelativeParent(ref.current.parentElement);
    const relativeRect = relativeParent
      ? relativeParent.getBoundingClientRect()
      : window.document.body.getBoundingClientRect();
    const selectionRect = getVisibleSelectionRect(window);
    if (!selectionRect) return;

    const position = {
      top: selectionRect.top - relativeRect.top + 20,
      left: selectionRect.left - relativeRect.left + selectionRect.width / 3,
      transform: 'translate(-50% scale(1)',
    };

    setPosition(position);
  };

  const handleRemoveLink = (
    e: BaseSyntheticEvent<
      MouseEvent,
      EventTarget & HTMLButtonElement,
      EventTarget
    >
  ) => {
    e.preventDefault();
    e.stopPropagation();
    removeLink(blockKey, entityKey, editorState, updateEditorState);
  };

  return (
    <div
      ref={ref as React.RefObject<HTMLDivElement>}
      className='z-10 absolute left-0  rounded-md  ring-1 ring-black ring-opacity-5 max-w-xl [ bg-white border border-gray-100 shadow-lg sm:rounded-lg px-1 py-1 sm:px-2 space-x-2 flex flex-row ] '
      style={position}>
      <a
        href={`https://${url}`}
        title={url}
        target='_blank'
        rel='noopener noreferrer'
        className='my-auto px-1 rounded-md truncate underline text-indigo-500 text-sm'>
        {url}
      </a>

      <button
        type='button'
        className='text-sm text-gray-700'
        onClick={handleRemoveLink}>
        <svg
          className='w-4 h-4'
          xmlns='http://www.w3.org/2000/svg'
          viewBox='0 0 20 20'
          fill='currentColor'>
          <path
            fillRule='evenodd'
            d='M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z'
            clipRule='evenodd'
          />
        </svg>
      </button>
    </div>
  );
};
